import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../helpers/apiUtils';
import RerunCopiesSection from './RerunCopiesSection';
import RerunPromoCopiesSection from './RerunPromoCopiesSection';
import RerunInviteTestSection from './RerunInviteTestSection';
import RerunInviteVsHeatupSection from './RerunInviteVsHeatupSection';
import { Abschnitt, GefahrBestaetigung, NurAdmin } from '../../components/marketingForm';
import InfoTip from '../../components/InfoTip';

/**
 * Wiederholungs-Webinar (`rerun`) — GLOBAL, nicht je Webinar: der eigene Raum spielt taeglich zum
 * Sendebeginn das letzte gelaufene Webinar, das den KI-Zeitcheck besteht (Plan
 * .claude/plans/2026-09-28-wiederholungs-webinar.md). Der Reiter zeigt Schalter, Plan fuer heute
 * und morgen, Sendungen mit Kennzahlen, Quellen mit KI-Befunden und legt Test-Sendungen an.
 *
 * Alle Zeitwerte kommen als Berliner Wandzeit `YYYY-MM-DD HH:mm:ss` vom Server und werden hier nur
 * als Zeichenkette verglichen und formatiert — keine Zeitzonenrechnung im Browser.
 */

const API = '/api/webinar-rerun-admin';
const POLL_MS = 60_000;
/** Wie der Server (routes/webinar-rerun-admin.ts): Sendebeginn nur in diesem Rahmen. */
const START_TIME_MIN = '18:00';
const START_TIME_MAX = '22:00';
const TEST_START_MIN = 1;
const TEST_START_MAX = 180;

type FindingKind = 'event' | 'frist' | 'harmlos';

interface Finding {
  zitat: string;
  art: FindingKind;
  event_datum: string | null;
  warum: string;
}

interface CheckView {
  id: number;
  status: 'pending' | 'done' | 'error';
  verdict: 'ok' | 'blocked' | null;
  airable_until: string | null;
  findings: Finding[];
  model: string | null;
  sources_used: string | null;
  prompt_version: string;
  override_by: string | null;
  override_at: string | null;
  attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

interface Candidate {
  webinar_id: string;
  title: string | null;
  start_date: string;
  duration_sec: number | null;
  video_status: string;
  video_ready: boolean;
  captions_status: string;
  captions_ready: boolean;
  offer_ready: boolean;
  rerun_allowed: boolean;
  offer_bonus_until: string | null;
  problem_tomorrow: string | null;
  check: CheckView | null;
}

interface AiringStats {
  registrations: number;
  channels: Record<string, number>;
  entered: number;
  played: number;
  watched30: number;
  offer_shown: number;
  offer_clicked: number;
  form_submit: number;
  orders: number;
  orders_test: number;
}

interface Airing {
  airing_id: string;
  kind: 'regular' | 'test';
  source_webinar_id: string;
  air_date: string;
  start_at: string;
  status: 'planned' | 'cancelled';
  cancel_reason: string | null;
  created_by: string | null;
  source_title: string | null;
  stats: AiringStats;
}

interface PlanDayView {
  air_date: string;
  start_at: string;
  action: 'create' | 'keep' | 'cancel' | 'skip';
  reason: string | null;
  source_webinar_id: string | null;
  source_title: string | null;
  existing: {
    airing_id: string;
    kind: 'regular' | 'test';
    status: 'planned' | 'cancelled';
    cancel_reason: string | null;
    source_webinar_id: string;
  } | null;
  registrations: number;
  skipped: Array<{ webinar_id: string; reason: string }>;
  pause: { webinar_id: string; start_date: string } | null;
}

interface RerunSettings {
  enabled: boolean;
  start_time: string;
  updated_by: string | null;
  updated_at: string | null;
}

interface Overview {
  settings: RerunSettings;
  server_now: string;
  today: string;
  tomorrow: string;
  tomorrow_start_at: string;
  viewer: { email: string | null; is_tester: boolean };
  plan: PlanDayView[];
  airings: Airing[];
  candidates: Candidate[];
}

interface ConfirmState {
  title: string;
  text: string;
  confirmLabel: string;
  color: 'primary' | 'error' | 'warning';
  run: () => Promise<unknown>;
}

interface TestResult {
  airing_id: string;
  source_webinar_id: string;
  start_at: string;
  invite_url: string;
}

// ---------------------------------------------------------------------------------------------
// Anzeige-Helfer (reine Zeichenketten, keine Zeitzone)
// ---------------------------------------------------------------------------------------------

/** `YYYY-MM-DD HH:mm(:ss)` → `TT.MM.JJJJ HH:MM Uhr`. */
function formatBerlin(s: string | null | undefined): string {
  if (!s) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]} Uhr` : s;
}

/** `YYYY-MM-DD` → `TT.MM.JJJJ`. */
function formatDate(s: string | null | undefined): string {
  if (!s) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

function timeOf(s: string): string {
  return s.slice(11, 16);
}

/** Wandzeit-Differenz in ms — beide Seiten im selben (Schein-UTC-)Rahmen, nur fuer Abstaende. */
function wallMs(s: string): number {
  return Date.parse(`${s.slice(0, 19).replace(' ', 'T')}Z`);
}

const REASON_TEXT: Record<string, string> = {
  ausgeschaltet: 'Wiederholung ist ausgeschaltet',
  keine_quelle: 'Kein gelaufenes Webinar erfüllt alle Bedingungen',
  heute_zu_spaet: 'Nach 16:00 Uhr wird für heute keine Sendung mehr angelegt',
  abgesagt: 'Die Sendung für diesen Tag wurde abgesagt — der Planer legt keine neue an',
  ki_befund: 'KI-Prüfung: Befund (Event-Bezug)',
  ki_check_laeuft: 'KI-Prüfung läuft noch',
  ki_check_fehlt: 'KI-Prüfung fehlt noch',
  ki_check_fehlgeschlagen: 'KI-Prüfung fehlgeschlagen',
  video_nicht_bereit: 'Raum-Video nicht fertig',
  untertitel_fehlen: 'Untertitel fehlen',
  angebot_fehlt: 'Angebot aus oder ohne Kaufweg',
  oeffentlicher_titel_fehlt: 'Öffentlicher Titel fehlt (im Webinar-Formular setzen)',
  noch_nicht_gelaufen: 'Original ist noch nicht gelaufen',
  zu_alt: 'Älter als 60 Tage',
  ausgeschlossen: 'Für die Wiederholung ausgeschlossen',
  kennung_nicht_6_zeichen: 'Webinar-Kennung hat nicht 6 Zeichen',
  kennung_unbaubar: 'Kennung der Sendung nicht baubar',
  schalter_aus: 'Wiederholung war ausgeschaltet',
  quelle_fehlt: 'Quellwebinar fehlt',
};

/** Grund-Code des Planers/Stores → deutscher Satz. Unbekannte Codes erscheinen roh (ehrlich statt leer). */
function reasonText(code: string | null | undefined): string {
  if (!code) return '';
  if (code.startsWith('pause:')) return `Pause — für Webinar ${code.slice(6)} wird geworben`;
  if (code.startsWith('ki_event_ab_')) {
    return `KI-Prüfung: Inhalt überholt ab ${formatDate(code.slice(12))}`;
  }
  if (code.startsWith('bereitschaft:')) return `17-Uhr-Prüfung: ${reasonText(code.slice(13))}`;
  if (code.startsWith('dashboard')) return `Im Dashboard abgesagt${code.slice(9)}`;
  return REASON_TEXT[code] || code;
}

const CHANNEL_LABEL: Record<string, string> = {
  WLC: 'Welcome',
  DOI: 'DOI-Seite',
  TST: 'Test',
  NL: 'Newsletter',
  NF: 'Nachfass',
  '-': 'ohne Kanal',
};

function channelSummary(channels: Record<string, number>): string {
  const parts = Object.entries(channels)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([ch, n]) => `${CHANNEL_LABEL[ch] || ch} ${n}`);
  return parts.join(' · ');
}

type ChipColor = 'default' | 'success' | 'info' | 'warning' | 'error';

function planChip(day: PlanDayView): { label: string; color: ChipColor } {
  if (day.action === 'create') return { label: 'Wird angelegt', color: 'info' };
  if (day.action === 'keep') return { label: 'Angelegt', color: 'success' };
  if (day.action === 'cancel') return { label: 'Wird abgesagt', color: 'warning' };
  const r = day.reason || '';
  if (r === 'ausgeschaltet') return { label: 'Ausgeschaltet', color: 'default' };
  if (r.startsWith('pause:')) return { label: 'Pause', color: 'warning' };
  if (r === 'keine_quelle') return { label: 'Keine Quelle', color: 'error' };
  if (r === 'heute_zu_spaet') return { label: 'Zu spät für heute', color: 'default' };
  if (r === 'abgesagt') return { label: 'Abgesagt', color: 'default' };
  return { label: 'Keine Sendung', color: 'default' };
}

const FINDING_CHIP: Record<FindingKind, { label: string; color: ChipColor }> = {
  event: { label: 'Event', color: 'error' },
  frist: { label: 'Frist', color: 'warning' },
  harmlos: { label: 'harmlos', color: 'default' },
};

function checkChip(check: CheckView | null): { label: string; color: ChipColor } {
  if (!check) return { label: 'Kein Check', color: 'default' };
  if (check.status === 'pending') return { label: 'Prüfung läuft', color: 'info' };
  if (check.status === 'error') return { label: 'Fehlgeschlagen', color: 'error' };
  if (check.verdict === 'blocked') return { label: 'Befund', color: 'error' };
  return { label: 'OK', color: 'success' };
}

/** Freigabe waere wirksam: Ergebnis liegt vor und sperrt (Befund, Fehlschlag oder Event-Grenze). */
function checkBlocks(check: CheckView | null): boolean {
  if (!check || check.status === 'pending' || check.override_by) return false;
  return check.status === 'error' || check.verdict === 'blocked' || check.airable_until != null;
}

function copyToClipboard(text: string): void {
  navigator.clipboard?.writeText(text).catch(() => undefined);
}

const headCellSx = { backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' } as const;

/**
 * Ein Satz je Tag für den Kopf (Plan 2026-10-22, B2): was läuft, und ob etwas zu tun ist. Liest nur den Plan
 * des Servers — keine eigene Regel.
 */
function tagesSatz(
  day: PlanDayView,
  label: string,
  now: string,
  enabled: boolean,
): { severity: 'success' | 'info' | 'warning'; text: string } {
  const zeit = `${label} ${timeOf(day.start_at)} Uhr`;
  const titel = day.source_title || day.source_webinar_id || '';
  if (day.action === 'keep' && day.existing?.status === 'planned') {
    if (day.start_at > now) {
      return { severity: 'success', text: `${zeit} läuft „${titel}“ als Wiederholung — nichts zu tun.` };
    }
    return { severity: 'success', text: `${label} lief bzw. läuft „${titel}“ als Wiederholung — nichts zu tun.` };
  }
  if (day.action === 'create') {
    return {
      severity: 'success',
      text: `${zeit} läuft „${titel}“ als Wiederholung (wird beim nächsten Planerlauf angelegt) — nichts zu tun.`,
    };
  }
  if (day.action === 'cancel') {
    return { severity: 'info', text: `${label}: die Sendung wird abgesagt — ${reasonText(day.reason)}.` };
  }
  const r = day.reason || '';
  if (!enabled || r === 'ausgeschaltet') {
    return { severity: 'info', text: `${label} keine Wiederholung — sie ist ausgeschaltet.` };
  }
  if (r.startsWith('pause:')) {
    return { severity: 'info', text: `${label} keine Wiederholung: Pause, weil für ein neues Webinar geworben wird — nichts zu tun.` };
  }
  if (r === 'heute_zu_spaet') return { severity: 'info', text: `${label} keine Wiederholung mehr (nach 16:00 Uhr wird nichts angelegt).` };
  if (r === 'abgesagt') return { severity: 'info', text: `${label} keine Wiederholung — die Sendung wurde abgesagt.` };
  if (r === 'keine_quelle') {
    return {
      severity: 'warning',
      text: `${label} keine Wiederholung: kein gelaufenes Webinar erfüllt alle Bedingungen. Was je Webinar fehlt, steht unten unter „Quellen und KI-Prüfung“ (Spalte „Morgen“).`,
    };
  }
  return { severity: 'info', text: `${label} keine Wiederholung: ${reasonText(r)}.` };
}

/** KI-Befunde einer Quelle: eingeklappt; „harmlos“ erst hinter „alle Zitate zeigen“. */
const BefundZelle: React.FC<{ findings: Finding[]; done: boolean }> = ({ findings, done }) => {
  const [offen, setOffen] = useState(false);
  const [alle, setAlle] = useState(false);
  if (findings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {done ? 'Keine' : '–'}
      </Typography>
    );
  }
  const zaehle = (k: FindingKind) => findings.filter((f) => f.art === k).length;
  const wichtig = findings.filter((f) => f.art !== 'harmlos');
  const harmlos = findings.length - wichtig.length;
  const zeigen = alle ? findings : wichtig;
  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {zaehle('event') > 0 && <Chip size="small" color="error" label={`${zaehle('event')} Event`} />}
        {zaehle('frist') > 0 && <Chip size="small" color="warning" label={`${zaehle('frist')} Frist`} />}
        {harmlos > 0 && <Chip size="small" label={`${harmlos} harmlos`} />}
        <Button size="small" onClick={() => setOffen((o) => !o)}>
          {offen ? 'Befunde ausblenden' : 'Befunde zeigen'}
        </Button>
      </Stack>
      {offen && (
        <Stack spacing={1} mt={1}>
          {zeigen.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Nur harmlose Zitate — sie sperren nichts.
            </Typography>
          )}
          {zeigen.map((f, i) => {
            const fc = FINDING_CHIP[f.art] || { label: f.art, color: 'default' as ChipColor };
            return (
              // Befunde haben keine eigene Kennung; die Liste wird nur ganz ersetzt.
              <Box key={i}>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={fc.label} color={fc.color} />
                  {f.event_datum && <Typography variant="caption">Event am {formatDate(f.event_datum)}</Typography>}
                </Stack>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  „{f.zitat}“
                </Typography>
                {f.warum && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {f.warum}
                  </Typography>
                )}
              </Box>
            );
          })}
          {harmlos > 0 && (
            <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setAlle((a) => !a)}>
              {alle ? 'Harmlose Zitate ausblenden' : `Alle Zitate zeigen (+${harmlos} harmlos)`}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------------------------

const RerunTab: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [testSource, setTestSource] = useState('');
  const [testMinutes, setTestMinutes] = useState('5');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [absageFuer, setAbsageFuer] = useState<Airing | null>(null);
  const [absageLaeuft, setAbsageLaeuft] = useState(false);
  const [ausschaltenOffen, setAusschaltenOffen] = useState(false);
  const [ausschaltenLaeuft, setAusschaltenLaeuft] = useState(false);
  // Nur die JUENGSTE Antwort gilt (Polling + Klick koennen sich ueberholen); nach dem Unmount nichts setzen.
  const seqRef = useRef(0);
  const aliveRef = useRef(true);
  // Sendebeginn vom Server nachziehen, solange niemand im Feld tippt — sonst zeigte es nach der Aenderung
  // einer zweiten Person den alten Wert, und „Speichern" schriebe ihn zurueck.
  const startTimeDirtyRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async (initForm = false) => {
    const seq = ++seqRef.current;
    setRefreshing(true);
    const r = await fetchData<Overview>(`${API}/overview`);
    if (!aliveRef.current || seq !== seqRef.current) return;
    setRefreshing(false);
    if (!r.success || !r.data) {
      // Alte Daten bleiben stehen — die Meldung sagt, dass sie nicht mehr frisch sind.
      setLoadError(r.error || 'Übersicht konnte nicht geladen werden');
      return;
    }
    const d = r.data;
    setLoadError(null);
    setData(d);
    if (initForm || !startTimeDirtyRef.current) setStartTime((d.settings.start_time || '18:00:00').slice(0, 5));
    setTestSource((cur) => cur || d.candidates.find((c) => c.video_ready)?.webinar_id || '');
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // Alle 60 s nachladen, solange das Fenster sichtbar ist; beim Zurueckkehren sofort.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  /** Eine Aktion ausfuehren, Meldung setzen, danach frisch laden. `true` = Erfolg. */
  const runAction = async <T,>(
    key: string,
    call: () => Promise<{ success: boolean; data?: T; error?: string }>,
    okText: string | ((data: T | undefined) => string),
  ): Promise<boolean> => {
    setBusy(key);
    setMessage(null);
    const r = await call();
    if (!aliveRef.current) return false;
    setBusy(null);
    if (!r.success) {
      setMessage({ text: r.error || 'Aktion fehlgeschlagen', severity: 'error' });
      return false;
    }
    setMessage({ text: typeof okText === 'function' ? okText(r.data) : okText, severity: 'success' });
    await load();
    return true;
  };

  const saveEnabled = (enabled: boolean) =>
    runAction(
      'enabled',
      () => fetchData(`${API}/settings`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
      enabled ? 'Wiederholung ist eingeschaltet.' : 'Wiederholung ist ausgeschaltet.',
    );

  const onToggleEnabled = (checked: boolean) => {
    if (busy) return;
    if (!checked) {
      // Seit 22.10. (Persona-Runde 3): auch Ausschalten fragt nach — der Schalter gilt fuer ALLE Webinare und
      // wirkt sofort. Eine einfache Bestaetigung, kein Tippen: als Notbremse muss er schnell bleiben.
      setAusschaltenOffen(true);
      return;
    }
    setConfirm({
      title: 'Wiederholung einschalten?',
      text:
        'Ab dann legt der Planer täglich Sendungen an (für heute bis 16:00 Uhr und für morgen), sobald keine Pause ist ' +
        'und eine Quelle alle Bedingungen samt KI-Prüfung erfüllt. Reguläre Sendungen sind dann im Raum erreichbar.',
      confirmLabel: 'Einschalten',
      color: 'primary',
      run: () => saveEnabled(true),
    });
  };

  const startTimeProblem = (() => {
    if (!/^\d{2}:\d{2}$/.test(startTime)) return 'Bitte als HH:MM';
    if (startTime < START_TIME_MIN || startTime > START_TIME_MAX) {
      return `Zwischen ${START_TIME_MIN} und ${START_TIME_MAX} Uhr`;
    }
    return null;
  })();
  const startTimeChanged = Boolean(data) && startTime !== (data?.settings.start_time || '').slice(0, 5);

  const saveStartTime = async () => {
    if (busy || startTimeProblem || !startTimeChanged) return;
    // Nach dem Speichern gilt wieder der Serverwert (runAction laedt neu).
    startTimeDirtyRef.current = false;
    await runAction(
      'start_time',
      () => fetchData(`${API}/settings`, { method: 'PUT', body: JSON.stringify({ start_time: startTime }) }),
      'Sendebeginn gespeichert — gilt für neu angelegte Sendungen.',
    );
  };

  // Absagen mit Rückfrage samt Wirkung (Plan 2026-10-22, F8: einfache Rückfrage, kein Tippen). Gleicher Request wie vorher.
  const askCancelAiring = (a: Airing) => setAbsageFuer(a);
  const absageBestaetigt = async () => {
    const a = absageFuer;
    if (!a || absageLaeuft) return;
    setAbsageLaeuft(true);
    await runAction(
      `cancel:${a.airing_id}`,
      () =>
        fetchData(`${API}/airings/${encodeURIComponent(a.airing_id)}/cancel`, {
          method: 'POST',
          body: JSON.stringify({}),
        }),
      `Sendung ${a.airing_id} abgesagt.`,
    );
    if (!aliveRef.current) return;
    setAbsageLaeuft(false);
    setAbsageFuer(null);
  };

  const askOverride = (c: Candidate, on: boolean) => {
    const check = c.check;
    if (!check) return;
    setConfirm({
      title: on ? 'Befund freigeben?' : 'Freigabe zurücknehmen?',
      text: on
        ? `${c.title || c.webinar_id}: Die Quelle darf dann trotz Befund laufen — auch über ein von der KI genanntes ` +
          'Event-Datum hinaus. Die Freigabe gilt nur für diesen Prüfstand; ändert sich das Material, wird neu geprüft.'
        : `${c.title || c.webinar_id}: Ohne Freigabe gilt wieder das Ergebnis der KI-Prüfung.`,
      confirmLabel: on ? 'Freigeben' : 'Zurücknehmen',
      color: on ? 'warning' : 'primary',
      run: () =>
        runAction(
          `override:${check.id}`,
          () =>
            fetchData(`${API}/checks/${check.id}/override`, { method: 'POST', body: JSON.stringify({ on }) }),
          on ? 'Befund freigegeben.' : 'Freigabe zurückgenommen.',
        ),
    });
  };

  // Der Server setzt den juengsten Check zurueck (samt Freigabe); bis zum neuen Ergebnis sperrt er.
  const askRecheck = (c: Candidate) => {
    setConfirm({
      title: 'Neu prüfen?',
      text:
        `${c.title || c.webinar_id}: Das bisherige Ergebnis der KI-Prüfung wird verworfen und neu ermittelt — ` +
        'bis dahin ist die Quelle gesperrt.' +
        (c.check?.override_by ? ' Die Freigabe wird dabei zurückgesetzt.' : ''),
      confirmLabel: 'Neu prüfen',
      color: 'primary',
      run: () =>
        runAction<{ reset: boolean; check_id: number | null }>(
          `recheck:${c.webinar_id}`,
          () =>
            fetchData(`${API}/sources/${encodeURIComponent(c.webinar_id)}/recheck`, {
              method: 'POST',
              body: JSON.stringify({}),
            }),
          (d) =>
            d?.reset
              ? `Neue Prüfung für ${c.webinar_id} angestoßen.`
              : `Für ${c.webinar_id} gibt es noch keinen Check — den legt der nächste Prüflauf an.`,
        ),
    });
  };

  const setAllowed = (c: Candidate, allowed: boolean) =>
    runAction(
      `allowed:${c.webinar_id}`,
      () =>
        fetchData(`${API}/sources/${encodeURIComponent(c.webinar_id)}/allowed`, {
          method: 'PUT',
          body: JSON.stringify({ allowed }),
        }),
      allowed
        ? `${c.webinar_id} darf wiederholt werden.`
        : `${c.webinar_id} wird nicht mehr als Wiederholung gesendet.`,
    );

  const testMinutesNum = Number(testMinutes);
  const testMinutesProblem =
    !Number.isInteger(testMinutesNum) || testMinutesNum < TEST_START_MIN || testMinutesNum > TEST_START_MAX
      ? `${TEST_START_MIN} bis ${TEST_START_MAX} Minuten`
      : null;

  const createTestAiring = async () => {
    if (busy || !data?.viewer.is_tester || !testSource || testMinutesProblem) return;
    setBusy('test');
    setMessage(null);
    setTestResult(null);
    const r = await fetchData<TestResult>(`${API}/test-airing`, {
      method: 'POST',
      body: JSON.stringify({ source_webinar_id: testSource, start_in_min: testMinutesNum }),
    });
    if (!aliveRef.current) return;
    setBusy(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Test-Sendung konnte nicht angelegt werden', severity: 'error' });
      return;
    }
    setTestResult(r.data);
    setMessage({ text: `Test-Sendung ${r.data.airing_id} angelegt.`, severity: 'success' });
    await load();
  };

  const onConfirm = async () => {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    await confirm.run();
    if (!aliveRef.current) return;
    setConfirmBusy(false);
    setConfirm(null);
  };

  // ------------------------------------------------------------------------------------------

  if (!data) {
    return loadError ? (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => load(true)}>
            Erneut laden
          </Button>
        }
      >
        {loadError}
      </Alert>
    ) : (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} /> <Typography>Wiederholung wird geladen …</Typography>
      </Box>
    );
  }

  const s = data.settings;
  const now = data.server_now;
  const durationBySource = new Map(data.candidates.map((c) => [c.webinar_id, c.duration_sec]));
  const airingsNewestFirst = [...data.airings].sort((a, b) => (a.start_at < b.start_at ? 1 : -1));
  const testSources = data.candidates.filter((c) => c.video_ready);

  const airingStatus = (a: Airing): { label: string; color: ChipColor } => {
    if (a.status === 'cancelled') return { label: 'Abgesagt', color: 'default' };
    if (a.start_at > now) return { label: 'Geplant', color: 'info' };
    const dur = durationBySource.get(a.source_webinar_id);
    if (!dur) return { label: 'Gestartet', color: 'success' };
    return wallMs(now) < wallMs(a.start_at) + dur * 1000
      ? { label: 'Läuft', color: 'warning' }
      : { label: 'Gelaufen', color: 'success' };
  };

  return (
    <Stack spacing={2}>
      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {loadError && (
        <Alert severity="warning">
          Aktualisieren fehlgeschlagen ({loadError}) — angezeigt wird der Stand von {formatBerlin(now)}.
        </Alert>
      )}

      {/* Zusammenfassung in Klartext — das Wichtigste zuerst */}
      {(() => {
        const heute = data.plan.find((d) => d.air_date === data.today);
        const morgen = data.plan.find((d) => d.air_date === data.tomorrow);
        const saetze = [
          heute ? tagesSatz(heute, 'Heute', now, s.enabled) : null,
          morgen ? tagesSatz(morgen, 'Morgen', now, s.enabled) : null,
        ].filter((x): x is { severity: 'success' | 'info' | 'warning'; text: string } => x !== null);
        if (saetze.length === 0) return null;
        const schwer = saetze.some((x) => x.severity === 'warning')
          ? 'warning'
          : saetze.some((x) => x.severity === 'success')
            ? 'success'
            : 'info';
        return (
          <Alert severity={schwer}>
            {saetze.map((x) => (
              <div key={x.text}>{x.text}</div>
            ))}
          </Alert>
        );
      })()}

      {/* Kopf: Schalter und Sendebeginn */}
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Box maxWidth={720}>
            <Typography variant="h5">Wiederholungs-Webinar</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Der eigene Raum spielt täglich zum Sendebeginn das letzte gelaufene Webinar, das alle Bedingungen und die
              KI-Prüfung besteht — mit eigener Kennung je Sendetag. Pause, solange für ein neues Webinar geworben
              wird. Läuft automatisch; Marketing muss hier normalerweise nichts tun.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Neue Webinare haben Vorrang: Sobald ein neues Webinar beworben werden darf und in den nächsten 7 Tagen
              beginnt, pausiert die Wiederholung automatisch bis zu dessen Ende — zum Termin des neuen Webinars läuft
              also keine Wiederholung parallel. Ausnahme: eine schon angelegte Wiederholung, für die sich bereits jemand
              angemeldet hat, läuft noch.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {s.updated_at
                ? `Zuletzt geändert von ${s.updated_by || 'unbekannt'} am ${formatBerlin(s.updated_at)}`
                : 'Noch nie geändert'}
              {` · Stand ${formatBerlin(now)}`}
            </Typography>
          </Box>
          <Stack spacing={1} alignItems={{ md: 'flex-end' }}>
            <Box sx={{ border: '1px solid #ffd8a8', bgcolor: '#fff8ef', borderRadius: '6px', p: 1.25, maxWidth: 360 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch checked={s.enabled} disabled={busy !== null} onChange={(e) => onToggleEnabled(e.target.checked)} />
                  }
                  label={s.enabled ? 'Wiederholung aktiv' : 'Wiederholung aus'}
                />
                <Chip size="small" color="warning" variant="outlined" label="gilt für alle Webinare" />
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div">
                Ein Schalter für die ganze Wiederholung, wirkt sofort. An: der Planer legt täglich eine Sendung an, wenn
                eine Quelle passt. Aus: es werden keine neuen Sendungen angelegt, und schon angelegte reguläre Sendungen
                laufen nicht — Angemeldete landen über ihren Link auf der Aufzeichnung. Test-Sendungen laufen weiter.
                Beides fragt vorher nach.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Sendebeginn (Berlin)"
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => {
                  startTimeDirtyRef.current = true;
                  setStartTime(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                error={Boolean(startTimeProblem)}
                helperText={startTimeProblem || 'Gilt für neu angelegte Sendungen'}
                sx={{ width: 190 }}
              />
              <Button
                variant="outlined"
                onClick={saveStartTime}
                disabled={busy !== null || Boolean(startTimeProblem) || !startTimeChanged}
                sx={{ mt: 0.25 }}
              >
                Speichern
              </Button>
            </Stack>
            <Button
              size="small"
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={() => load()}
              disabled={refreshing}
            >
              Aktualisieren
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Plan heute / morgen */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Plan heute und morgen
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          So entscheidet der Planer gerade (er läuft alle 15 Minuten und legt je Tag höchstens eine reguläre Sendung an).
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {data.plan.map((day) => {
            const chip = planChip(day);
            const dayLabel = day.air_date === data.today ? 'Heute' : day.air_date === data.tomorrow ? 'Morgen' : '';
            return (
              <Paper key={day.air_date} variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {dayLabel} {formatDate(day.air_date)}, {timeOf(day.start_at)} Uhr
                  </Typography>
                  <Chip size="small" label={chip.label} color={chip.color} />
                  {day.existing && (
                    <Chip size="small" variant="outlined" label={day.existing.airing_id} sx={{ fontFamily: 'monospace' }} />
                  )}
                </Stack>
                {day.reason && (
                  <Typography variant="body2" mb={0.5}>
                    {reasonText(day.reason)}
                  </Typography>
                )}
                {day.action === 'create' && (
                  <Typography variant="body2" mb={0.5}>
                    Beim nächsten Planerlauf.
                  </Typography>
                )}
                {day.existing?.status === 'cancelled' && day.existing.cancel_reason && (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    {reasonText(day.existing.cancel_reason)}
                  </Typography>
                )}
                {day.source_webinar_id && (
                  <Typography variant="body2">
                    Quelle: {day.source_title || day.source_webinar_id}{' '}
                    <Typography component="span" variant="body2" color="text.secondary" fontFamily="monospace">
                      ({day.source_webinar_id})
                    </Typography>
                  </Typography>
                )}
                {day.existing && (
                  <Typography variant="body2">Anmeldungen: {day.registrations}</Typography>
                )}
                {day.pause && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Beworbenes Webinar {day.pause.webinar_id} am {formatBerlin(day.pause.start_date)}
                  </Typography>
                )}
                {day.skipped.length > 0 && (day.action === 'skip' || day.action === 'create') && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Übersprungene Quellen (jüngste zuerst):
                    </Typography>
                    {day.skipped.map((sk) => (
                      <Typography key={sk.webinar_id} variant="caption" color="text.secondary" display="block">
                        <Box component="span" fontFamily="monospace">
                          {sk.webinar_id}
                        </Box>
                        : {reasonText(sk.reason)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      {/* Sendungen — kompakt: Zahlen in EINER Zelle, „Absagen“ ohne Querscrollen sichtbar */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sendungen
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Letzte 14 und nächste 7 Tage, neueste oben. Anmeldungen = verschiedene Adressen (auch Späteinlass
          <InfoTip text="Späteinlass: Anmeldung erst nach dem Beginn der Sendung — wer noch während der Sendung kommt, wird mitgezählt." />
          ); im Raum ohne Proben
          <InfoTip text="Proben: Aufrufe über einen Test-Link (z. B. von uns beim Ausprobieren). Sie zählen bei „im Raum“ nicht mit." />
          . Bestellungen erscheinen erst nach der nächsten Bestell-Zuordnung
          <InfoTip text="Bestell-Zuordnung: ein regelmäßiger Abgleich ordnet neue Bestellungen der Sendung zu, aus der sie kamen. Bis dahin steht hier noch 0 — das ist kein Fehler." />{' '}
          und zählen ohne Testbestellungen.
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Datum / Zeit</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx} />
                <TableCell sx={headCellSx}>
                  Quelle
                  <InfoTip text="Darunter klein: Kürzel des Original-Webinars und die Kennung der Sendung. Reguläre Sendungen heißen Kürzel + Monat + Tag, z. B. sHD2zO1022 = Wiederholung von sHD2zO am 22.10.; Test-Sendungen beginnen mit T. Die Kennung steht in Links und Auswertungen." />
                </TableCell>
                <TableCell sx={headCellSx}>
                  Zahlen
                  <InfoTip text="Im Raum = so viele haben den Raum betreten. Gespielt = davon haben das Video gestartet. ≥ 30 s = davon mindestens 30 Sekunden geschaut. Angebot gesehen/geklickt = Kaufangebot im Raum eingeblendet bzw. angeklickt. Kanal = woher die Anmeldung kam (Welcome = Einladung aus der Welcome-Strecke für neue Abonnenten, DOI-Seite = Seite nach der Newsletter-Bestätigung, Newsletter, Nachfass = Nachfass-Mail, Test = Test-Link)." />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {airingsNewestFirst.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">Noch keine Sendungen</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                airingsNewestFirst.map((a) => {
                  const st = airingStatus(a);
                  const channels = channelSummary(a.stats.channels);
                  const canCancel = a.status === 'planned' && a.start_at > now;
                  return (
                    <TableRow key={a.airing_id} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatBerlin(a.start_at)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title={a.status === 'cancelled' ? reasonText(a.cancel_reason) : ''} arrow>
                            <Chip size="small" label={st.label} color={st.color} />
                          </Tooltip>
                          {a.kind === 'test' && (
                            <Tooltip title={a.created_by ? `angelegt von ${a.created_by}` : ''} arrow>
                              <Chip size="small" variant="outlined" label="Test" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {canCancel && (
                          <Tooltip
                            arrow
                            title={
                              `Fragt vorher nach. Die Sendung findet dann nicht statt` +
                              (a.stats.registrations > 0
                                ? ` — ${a.stats.registrations} Angemeldete landen über ihren Link auf der Aufzeichnung des Originals.`
                                : '.') +
                              (a.kind === 'regular'
                                ? ' Für diesen Tag legt der Planer keine neue Sendung an.'
                                : ' Die Test-Sendung ist danach im Raum nicht mehr erreichbar.')
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={busy !== null}
                                onClick={() => askCancelAiring(a)}
                              >
                                Absagen …
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 280 }} noWrap title={a.source_title || ''}>
                          {a.source_title || a.source_webinar_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {a.source_webinar_id} · {a.airing_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {a.stats.registrations} Anmeldungen · {a.stats.entered} im Raum · {a.stats.orders} Bestellungen
                          {a.stats.orders_test > 0 ? ` (+ ${a.stats.orders_test} Test)` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Gespielt {a.stats.played} · ≥ 30 s {a.stats.watched30} · Angebot gesehen/geklickt{' '}
                          {a.stats.offer_shown}/{a.stats.offer_clicked}
                          {a.stats.form_submit > 0 ? ` · Bestellknopf ${a.stats.form_submit}` : ''}
                        </Typography>
                        {channels && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Anmeldungen nach Kanal: {channels}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Quellen & KI-Zeitcheck — eingeklappt; die Zusammenfassung oben sagt, wann man hier hinein muss */}
      <Abschnitt
        titel="Quellen und KI-Prüfung"
        art="automatisch"
        eingeklappt
        erklaerung="Welche gelaufenen Webinare als Wiederholung in Frage kommen und was die KI-Prüfung gefunden hat. Nur nötig, wenn oben steht, dass keine Quelle passt — oder um ein Webinar auszuschließen."
      >
        <Typography variant="body2" color="text.secondary" mb={2}>
          Webinare mit Raum-Einrichtung der letzten 61 und nächsten 8 Tage. „Morgen“ zeigt, warum eine Quelle die
          Sendung am {formatDate(data.tomorrow)} um {timeOf(data.tomorrow_start_at)} Uhr nicht tragen könnte. Die
          KI-Prüfung sperrt nur bei Event-Bezug (Art „Event“); Fristen im Pitch werden nur gemeldet.
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Webinar</TableCell>
                <TableCell sx={headCellSx}>Morgen</TableCell>
                <TableCell sx={headCellSx}>KI-Prüfung</TableCell>
                <TableCell sx={headCellSx}>Befunde</TableCell>
                <TableCell sx={headCellSx}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.candidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">Keine Webinare mit Raum im Zeitfenster</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.candidates.map((c) => {
                  const cc = checkChip(c.check);
                  const findings = c.check?.findings || [];
                  return (
                    <TableRow key={c.webinar_id} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {c.title || c.webinar_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <Box component="span" fontFamily="monospace">
                            {c.webinar_id}
                          </Box>{' '}
                          · {formatBerlin(c.start_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Bonus in der Wiederholung:{' '}
                          {c.offer_bonus_until ? `bis ${formatBerlin(c.offer_bonus_until)}` : 'keiner'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {c.problem_tomorrow ? (
                          <Typography variant="body2">{reasonText(c.problem_tomorrow)}</Typography>
                        ) : (
                          <Chip size="small" color="success" label="Sendbar" />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={cc.label} color={cc.color} />
                          {c.check?.override_by && (
                            <Chip size="small" variant="outlined" color="success" label="Freigegeben" />
                          )}
                        </Stack>
                        {c.check?.airable_until && (
                          <Typography variant="caption" display="block" mt={0.5}>
                            Gesperrt ab {formatDate(c.check.airable_until)} (Event-Datum)
                          </Typography>
                        )}
                        {c.check?.override_by && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            von {c.check.override_by} am {formatBerlin(c.check.override_at)}
                          </Typography>
                        )}
                        {c.check && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {c.check.status === 'pending' ? `Versuch ${c.check.attempts}` : `Stand ${formatBerlin(c.check.updated_at)}`}
                          </Typography>
                        )}
                        <NurAdmin>
                          {c.check && (c.check.model || c.check.sources_used) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[c.check.model, c.check.sources_used].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          {c.check?.last_error && c.check.status !== 'done' && (
                            <Typography variant="caption" color="error" display="block" sx={{ wordBreak: 'break-word' }}>
                              {c.check.last_error}
                            </Typography>
                          )}
                        </NurAdmin>
                      </TableCell>
                      <TableCell sx={{ minWidth: 280 }}>
                        <BefundZelle findings={findings} done={c.check?.status === 'done'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Stack spacing={1} alignItems="flex-start">
                          {checkBlocks(c.check) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={busy !== null}
                              onClick={() => askOverride(c, true)}
                            >
                              Befund freigeben
                            </Button>
                          )}
                          {c.check?.override_by && (
                            <Button size="small" disabled={busy !== null} onClick={() => askOverride(c, false)}>
                              Freigabe zurücknehmen
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={busy === `recheck:${c.webinar_id}` ? <CircularProgress size={14} /> : undefined}
                            disabled={busy !== null}
                            onClick={() => askRecheck(c)}
                          >
                            Neu prüfen
                          </Button>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={c.rerun_allowed}
                                disabled={busy !== null}
                                onChange={(e) => setAllowed(c, e.target.checked)}
                              />
                            }
                            label={<Typography variant="body2">Für Wiederholung erlaubt</Typography>}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Abschnitt>

      {/* Texte und Auswertungen — eingeklappt: laufen automatisch, Marketing muss hier nichts tun */}
      <Abschnitt
        titel="Werbetexte der Wiederholung"
        art="automatisch"
        eingeklappt
        erklaerung="Einladungen, Anmeldeseite und Mails an Angemeldete — schreibt und prüft die KI selbst. Nur öffnen, um einen Text anzusehen oder von Hand zu ändern."
      >
        <RerunCopiesSection />
        <RerunPromoCopiesSection />
      </Abschnitt>
      <Abschnitt
        titel="Auswertungen: Einladungs-Test und Vergleich mit Heat-ups"
        art="optional"
        eingeklappt
        erklaerung="Statistik zu Versandzeit der Einladung und zum Vergleich mit den Original-Heat-ups. Nichts zu tun."
      >
        <RerunInviteTestSection />
        <RerunInviteVsHeatupSection />
      </Abschnitt>

      {/* Test-Sendung */}
      <Abschnitt
        titel="Test-Sendung anlegen"
        art="optional"
        eingeklappt
        erklaerung="Zum Ausprobieren: eine eigene Sendung, die in wenigen Minuten startet."
      >
        <Typography variant="body2" color="text.secondary" mb={2}>
          Legt eine eigene Sendung mit Test-Kennung an, die in wenigen Minuten startet — auch bei ausgeschalteter
          Wiederholung. Nur für Firmenadressen; Anmeldung im Raum mit dem Link.
        </Typography>
        {!data.viewer.is_tester && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Test-Sendungen legen nur Firmenadressen (@yes-investmedia.de, @finanztrends.de) an — angemeldet als{' '}
            {data.viewer.email || 'unbekannt'}.
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            select
            label="Quelle"
            size="small"
            value={testSources.some((c) => c.webinar_id === testSource) ? testSource : ''}
            onChange={(e) => setTestSource(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 320 }}
            helperText={testSources.length === 0 ? 'Kein Webinar mit fertigem Raum-Video im Zeitfenster' : ' '}
          >
            <MenuItem value="">Bitte wählen</MenuItem>
            {testSources.map((c) => (
              <MenuItem key={c.webinar_id} value={c.webinar_id}>
                {c.webinar_id} · {formatDate(c.start_date)} · {c.title || ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Start in Minuten"
            type="number"
            size="small"
            value={testMinutes}
            onChange={(e) => setTestMinutes(e.target.value)}
            inputProps={{ min: TEST_START_MIN, max: TEST_START_MAX, step: 1 }}
            error={Boolean(testMinutesProblem)}
            helperText={testMinutesProblem || ' '}
            sx={{ width: 170 }}
          />
          <Button
            variant="contained"
            onClick={createTestAiring}
            disabled={busy !== null || !data.viewer.is_tester || !testSource || Boolean(testMinutesProblem)}
            startIcon={busy === 'test' ? <CircularProgress size={14} /> : undefined}
          >
            Test-Sendung anlegen
          </Button>
        </Stack>
        {testResult && (
          <Stack spacing={0.5} mt={2}>
            <Typography variant="body2">
              Sendung <strong>{testResult.airing_id}</strong> (Quelle {testResult.source_webinar_id}) startet am{' '}
              {formatBerlin(testResult.start_at)}. Einladungs-Link mit deiner Adresse:
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {testResult.invite_url}
              </Typography>
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(testResult.invite_url)}>
                Kopieren
              </Button>
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                href={testResult.invite_url}
                target="_blank"
                rel="noopener"
              >
                Öffnen
              </Button>
            </Stack>
          </Stack>
        )}
      </Abschnitt>

      <GefahrBestaetigung
        open={ausschaltenOffen}
        titel="Wiederholung für alle Webinare ausschalten?"
        wirkung="Wirkt sofort und für alle Webinare: Es werden keine neuen Sendungen angelegt, und schon angelegte reguläre Sendungen laufen nicht — Angemeldete landen über ihren Link auf der Aufzeichnung des Originals. Test-Sendungen laufen weiter. Wieder einschalten geht jederzeit über denselben Schalter."
        knopfText="Ausschalten"
        laeuft={ausschaltenLaeuft}
        onBestaetigen={async () => {
          if (ausschaltenLaeuft) return;
          setAusschaltenLaeuft(true);
          await saveEnabled(false);
          if (!aliveRef.current) return;
          setAusschaltenLaeuft(false);
          setAusschaltenOffen(false);
        }}
        onAbbrechen={() => setAusschaltenOffen(false)}
      />

      <GefahrBestaetigung
        open={Boolean(absageFuer)}
        titel="Sendung absagen?"
        wirkung={
          absageFuer ? (
            <>
              Sendung am {formatBerlin(absageFuer.start_at)} („{absageFuer.source_title || absageFuer.source_webinar_id}“)
              wird abgesagt.{' '}
              {absageFuer.stats.registrations > 0
                ? `${absageFuer.stats.registrations} Angemeldete landen über ihren Link dann auf der Aufzeichnung des Originals statt in der Sendung. `
                : ''}
              {absageFuer.kind === 'regular'
                ? 'Für diesen Tag legt der Planer keine neue Sendung an.'
                : 'Die Test-Sendung ist danach im Raum nicht mehr erreichbar.'}
            </>
          ) : (
            ''
          )
        }
        knopfText="Sendung absagen"
        laeuft={absageLaeuft}
        onBestaetigen={absageBestaetigt}
        onAbbrechen={() => setAbsageFuer(null)}
      />

      <Dialog open={Boolean(confirm)} onClose={() => (confirmBusy ? undefined : setConfirm(null))} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm?.text}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={confirm?.color || 'primary'}
            onClick={onConfirm}
            disabled={confirmBusy}
            startIcon={confirmBusy ? <CircularProgress size={14} /> : undefined}
          >
            {confirm?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default RerunTab;
